import {
  CLIENT_END_REASONS, CONNECTION_STATES, DIAGNOSTIC_LIMITS, DTLS_STATES, ICE_STATES, SIGNALING_STATES,
  type ClientEndReason, type DiagnosticMetrics, type DiagnosticReport, type DiagnosticReportKind,
} from '@bcio-voice/contracts';

/**
 * Bounded per-call media-health reporter (POST /api/v1/calls/:id/diagnostics).
 *
 * Only allowlisted counters and fixed transport-state enums leave the device. It never reads or sends SDP,
 * ICE candidates, ufrag/pwd, certificate fingerprints, addresses, tokens, cookies or raw error text: the
 * stats parser copies named numeric fields from audio RTP/candidate-pair/transport entries and nothing else.
 */
export type DiagnosticPost = (callId: string, report: DiagnosticReport, signal?: AbortSignal) => Promise<unknown>;
export type RawStats = Iterable<Readonly<Record<string, unknown>>>;

const END_REASONS = new Set<string>(CLIENT_END_REASONS);
const count = (v: unknown) => (Number.isSafeInteger(v) && (v as number) >= 0 && (v as number) <= 999_999_999_999_999 ? (v as number) : undefined);
const ms = (seconds: unknown) => (typeof seconds === 'number' && Number.isFinite(seconds) && seconds >= 0 && seconds <= 600 ? Math.round(seconds * 1_000_000) / 1000 : undefined);
const state = <T extends readonly string[]>(allowed: T, v: unknown): T[number] | undefined => (typeof v === 'string' && (allowed as readonly string[]).includes(v) ? v : undefined);

/** Map an engine end reason to the contract list; `server_ended`/`server_failed` keep their prefix meaning. */
export function endReasonCode(reason: string | null | undefined): ClientEndReason {
  if (!reason) return 'unknown';
  if (END_REASONS.has(reason)) return reason as ClientEndReason;
  if (reason.startsWith('server_')) return reason === 'server_failed' ? 'server_failed' : 'server_ended';
  return 'unknown';
}

/** Allowlisted copy of caller-supplied metrics. Unknown keys and out-of-range or non-enum values are dropped. */
export function sanitizeDiagnosticMetrics(input: Readonly<Record<string, unknown>> | null | undefined): DiagnosticMetrics {
  const v = input ?? {};
  const out: Record<string, number | string> = {};
  const put = (key: string, value: number | string | undefined) => { if (value !== undefined) out[key] = value; };
  for (const key of ['packets_sent', 'packets_received', 'packets_lost', 'bytes_sent', 'bytes_received', 'reconnects'] as const) put(key, count(v[key]));
  for (const key of ['jitter_ms', 'rtt_ms'] as const) { const x = v[key]; if (typeof x === 'number' && Number.isFinite(x) && x >= 0 && x <= 600_000) put(key, Math.round(x * 1000) / 1000); }
  const level = v.audio_level_dbfs;
  if (typeof level === 'number' && Number.isFinite(level) && level >= -200 && level <= 0) put('audio_level_dbfs', Math.round(level * 100) / 100);
  put('ice_state', state(ICE_STATES, v.ice_state));
  put('dtls_state', state(DTLS_STATES, v.dtls_state));
  put('connection_state', state(CONNECTION_STATES, v.connection_state));
  put('signaling_state', state(SIGNALING_STATES, v.signaling_state));
  return out as DiagnosticMetrics;
}

/** Reduce an RTCStatsReport (as entries) to the allowlisted metrics. Addresses, candidate IDs and fingerprints are never read. */
export function metricsFromStats(stats: RawStats | null | undefined): DiagnosticMetrics {
  const out: Record<string, unknown> = {};
  if (!stats) return {};
  for (const entry of stats) {
    const kind = entry.kind ?? entry.mediaType;
    if (entry.type === 'outbound-rtp' && kind === 'audio') { out.packets_sent = entry.packetsSent; out.bytes_sent = entry.bytesSent; }
    else if (entry.type === 'inbound-rtp' && kind === 'audio') {
      out.packets_received = entry.packetsReceived; out.bytes_received = entry.bytesReceived; out.jitter_ms = ms(entry.jitter);
      out.packets_lost = typeof entry.packetsLost === 'number' ? Math.max(0, entry.packetsLost) : undefined;
      const level = entry.audioLevel;
      if (typeof level === 'number' && Number.isFinite(level)) out.audio_level_dbfs = level <= 0 ? -200 : Math.max(-200, Math.min(0, 20 * Math.log10(level)));
    } else if (entry.type === 'candidate-pair' && (entry.nominated === true || entry.selected === true) && entry.state === 'succeeded') out.rtt_ms = ms(entry.currentRoundTripTime);
    else if (entry.type === 'transport') {
      out.dtls_state = entry.dtlsState;
      out.ice_state = entry.iceState;
    }
  }
  return sanitizeDiagnosticMetrics(out);
}

export interface DiagnosticReporterOptions {
  readonly deviceId: string;
  readonly post: DiagnosticPost;
  readonly maxPerCall?: number;
  readonly maxBytes?: number;
}

/** Enforces the server bounds locally (<= 20 reports per call, <= 4 KiB) so the device never spends them. */
export class DiagnosticReporter {
  private readonly counts = new Map<string, number>();
  private readonly maxPerCall: number;
  private readonly maxBytes: number;
  constructor(private readonly options: DiagnosticReporterOptions) {
    this.maxPerCall = Math.min(options.maxPerCall ?? DIAGNOSTIC_LIMITS.max_reports_per_call_device, DIAGNOSTIC_LIMITS.max_reports_per_call_device);
    this.maxBytes = Math.min(options.maxBytes ?? DIAGNOSTIC_LIMITS.max_body_bytes, DIAGNOSTIC_LIMITS.max_body_bytes);
  }
  sent(callId: string): number { return this.counts.get(callId) ?? 0; }
  build(kind: DiagnosticReportKind, input: { readonly end_reason?: string | null; readonly metrics?: DiagnosticMetrics | Readonly<Record<string, unknown>> | null } = {}): DiagnosticReport {
    const metrics = sanitizeDiagnosticMetrics(input.metrics as Readonly<Record<string, unknown>> | null | undefined);
    return {
      device_id: this.options.deviceId, kind,
      ...(kind === 'teardown' ? { end_reason: endReasonCode(input.end_reason) } : {}),
      ...(Object.keys(metrics).length ? { metrics } : {}),
    };
  }
  /** Resolves false when over budget or when the post fails; never throws into call handling. */
  async send(callId: string, kind: DiagnosticReportKind, input: Parameters<DiagnosticReporter['build']>[1] = {}, signal?: AbortSignal): Promise<boolean> {
    const used = this.counts.get(callId) ?? 0;
    if (used >= this.maxPerCall) return false;
    let report = this.build(kind, input);
    if (new TextEncoder().encode(JSON.stringify(report)).byteLength > this.maxBytes) report = this.build(kind, { end_reason: input.end_reason ?? null });
    this.counts.set(callId, used + 1);
    try { await this.options.post(callId, report, signal); return true; } catch { return false; }
  }
  forget(callId: string): void { this.counts.delete(callId); }
}
